// The session: tokens and profile in IndexedDB, never in localStorage
// (D-81); the device identifier kept across sessions; proactive refresh;
// every rejection state told apart and none of them touching the local
// queue (D-62, D-70).
//
// The manager owns the API client's session hooks and exposes a snapshot
// the screens subscribe to. The snapshot never carries a token.
//
// INC-13 (CA-PLT-05): a session may also be Aspen's support, opened by a link
// of one use: it reads the office of one organization, writes nothing, has no
// refresh token and no person, and ends at the hour the link was given for.
//
// INC-16.1 (D-424): the profile a session keeps is read again on its own, at
// the moments the organization may have changed under it — every start, a
// return after a while out of sight, the signal back —, and every reading of
// it replaces the stored one whole: what the organization is called, its
// brand and white label, its words, its modules, its policy of location, its
// timezone and locale. Nothing else of the device is touched.
import { ApiClient } from '../api/client';
import {
  ApiError,
  CODES,
  INVITATION_REFUSALS,
  NetworkError,
  SUPPORT_REFUSALS,
  isApiError,
  isNetworkError,
} from '../api/errors';
import type { BootstrapRead, MeResponse, SupportSession, TokenPair } from '../api/types';
import {
  getSessionRecord,
  ownPendingCount,
  ownWaitingCount,
  type FieldcoreDb,
  type TokensRecord,
} from '../db/database';

export type SessionStatus =
  'loading' | 'anonymous' | 'authenticated' | 'password_change_required' | 'revoked' | 'suspended';

export type BootstrapStatus = 'loading' | 'ok' | 'unknown_tenant' | 'unavailable';

export interface SessionSnapshot {
  status: SessionStatus;
  profile: MeResponse | null;
  deviceId: string | null;
  deviceLabel: string | null;
  bootstrap: BootstrapRead | null;
  bootstrapStatus: BootstrapStatus;
  online: boolean;
  /** INC-07b-2b-1 (D-290): why the session is anonymous, when it is because
   *  the server no longer takes it — a refresh refused, a token gone — and
   *  not because the person signed out. The office reads it to pass the
   *  person through the sign in and back to where they were; a sign out
   *  clears it. */
  expired: boolean;
  /** INC-13 (CA-PLT-05): the support session, when this is one: the instant it
   *  ends, which the office shows while it lasts. */
  support: SupportState | null;
  /** INC-13: when the server last refused a write of the support session, so
   *  the office says why; cleared when the person moves on. */
  supportRefusedAt: number | null;
}

export interface SupportState {
  /** The instant the session ends, ISO 8601, as the API said it. */
  endsAt: string;
}

/** INC-13: the support session is nobody. The profile the office reads names no
 *  person: an identifier no account has, and no name or address. */
export const SUPPORT_USER_ID = '00000000-0000-0000-0000-000000000000';

function supportProfile(session: SupportSession): MeResponse {
  return {
    user: {
      id: SUPPORT_USER_ID,
      email: '',
      full_name: '',
      role: session.role,
      client_id: null,
      status: 'active',
      last_login_at: null,
      password_updated_at: null,
    },
    role: session.role,
    organization: session.organization,
    modules: session.modules,
    canonical_hostname: session.canonical_hostname,
    password_change_required: false,
    device_id: null,
    // INC-15e (E4): a support session reads no profile, so no server version.
    product_version: '',
  };
}

/** Signing out is refused while the outbox holds anything not yet on the server. */
export class SignOutBlockedError extends Error {
  readonly pending: number;

  constructor(pending: number) {
    super(`sign out blocked: ${pending} pending`);
    this.name = 'SignOutBlockedError';
    this.pending = pending;
  }
}

/** The document whose visibility says when the application comes back. */
export type SessionVisibility = Pick<
  Document,
  'visibilityState' | 'addEventListener' | 'removeEventListener'
>;

export interface SessionManagerOptions {
  db: FieldcoreDb;
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  now?: () => number;
  isOnline?: () => boolean;
  /** Fraction of the token's life at which the proactive refresh fires: 20 %. */
  refreshAheadFraction?: number;
  /** INC-16.1: the tests give their own; the browser's by default, none outside one. */
  document?: SessionVisibility | null;
}

/** INC-16.1 (D-424): how long the application must have been out of sight for
 *  its return to read the profile again: the threshold its search for a new
 *  version keeps on returning (app/update.ts). */
export const PROFILE_RETURN_MS = 15 * 60 * 1000;

export const BOOTSTRAP_PATH = '/api/v1/app/bootstrap';
export const LOGIN_PATH = '/api/v1/auth/login';
export const REFRESH_PATH = '/api/v1/auth/refresh';
export const LOGOUT_PATH = '/api/v1/auth/logout';
export const ME_PATH = '/api/v1/auth/me';
export const PASSWORD_PATH = '/api/v1/auth/password';
// INC-07c-1 (D-258): where a person's link ends, public like login.
export const INVITATION_PATH = '/api/v1/auth/invitation';
// INC-12c-2 (D-291): where a person who forgot their password asks for a link.
export const RECOVERY_PATH = '/api/v1/auth/password-recovery';
// INC-13 (CA-PLT-05): where a support link is exchanged for a support session.
export const SUPPORT_PATH = '/api/v1/auth/support';

type Listener = (snapshot: SessionSnapshot) => void;

function defaultOnline(): boolean {
  if (typeof navigator === 'undefined') return true;
  // Typed as a boolean, absent at runtime in Node: unknown means online.
  const online: unknown = Reflect.get(navigator, 'onLine');
  return online !== false;
}

function browserDocument(): SessionVisibility | null {
  return typeof document === 'undefined' ? null : document;
}

/** INC-16.1: whether the session is one a person is in, the states its profile
 *  was read for. Every other one ended, or never began. */
export function isOpen(status: SessionStatus): boolean {
  return status === 'authenticated' || status === 'password_change_required';
}

export class SessionManager {
  readonly api: ApiClient;
  private readonly db: FieldcoreDb;
  private readonly now: () => number;
  private readonly isOnline: () => boolean;
  private readonly refreshAheadFraction: number;
  private readonly document: SessionVisibility | null;
  private tokens: TokensRecord | null = null;
  private refreshInflight: Promise<string | null> | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  /** INC-16.1: the reading of the profile in flight, one at a time. */
  private renewal: Promise<void> | null = null;
  /** INC-16.1: since when the application is out of sight; none while in view. */
  private hiddenSince: number | null = null;
  private snapshot: SessionSnapshot = {
    status: 'loading',
    profile: null,
    deviceId: null,
    deviceLabel: null,
    bootstrap: null,
    bootstrapStatus: 'loading',
    online: true,
    expired: false,
    support: null,
    supportRefusedAt: null,
  };
  private readonly listeners = new Set<Listener>();
  private readonly onOnline = (): void => {
    this.setSnapshot({ online: true });
    if (this.tokens !== null && this.refreshDue()) void this.refresh();
    // INC-16.1: what changed while there was no signal.
    void this.renewProfile();
  };
  private readonly onOffline = (): void => {
    this.setSnapshot({ online: false });
  };
  /** INC-16.1: back in sight after PROFILE_RETURN_MS or more out of it, the
   *  profile is read again; sooner, nothing is asked. */
  private readonly onVisibility = (): void => {
    if (this.document?.visibilityState === 'hidden') {
      this.hiddenSince ??= this.now();
      return;
    }
    const since = this.hiddenSince;
    this.hiddenSince = null;
    if (since !== null && this.now() - since >= PROFILE_RETURN_MS) void this.renewProfile();
  };

  constructor(options: SessionManagerOptions) {
    this.db = options.db;
    this.now = options.now ?? (() => Date.now());
    this.isOnline = options.isOnline ?? defaultOnline;
    this.refreshAheadFraction = options.refreshAheadFraction ?? 0.2;
    this.document = options.document === undefined ? browserDocument() : options.document;
    this.api = new ApiClient({
      baseUrl: options.baseUrl,
      fetchImpl: options.fetchImpl,
      hooks: {
        getAccessToken: () => Promise.resolve(this.tokens?.accessToken ?? null),
        refresh: () => this.refresh(),
        onProfileStale: () => this.loadProfile(),
        onSessionCode: (code, status) => this.handleSessionCode(code, status),
      },
    });
  }

  // --- Snapshot --------------------------------------------------------------------

  getSnapshot(): SessionSnapshot {
    return this.snapshot;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private setSnapshot(patch: Partial<SessionSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...patch };
    for (const listener of this.listeners) listener(this.snapshot);
  }

  // --- Start -------------------------------------------------------------------------

  /** Load what the stores hold and decide the initial state.
   *
   *  INC-16.1 (D-424): the screens paint at once with what is stored, and a
   *  session kept on the device has its profile read again behind them, an
   *  answer the start never waits for. */
  async init(): Promise<void> {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.onOnline);
      window.addEventListener('offline', this.onOffline);
    }
    this.document?.addEventListener('visibilitychange', this.onVisibility);
    if (this.document?.visibilityState === 'hidden') this.hiddenSince = this.now();
    const [tokens, profile, device, state, bootstrap] = await Promise.all([
      getSessionRecord(this.db, 'tokens'),
      getSessionRecord(this.db, 'profile'),
      getSessionRecord(this.db, 'device'),
      getSessionRecord(this.db, 'state'),
      getSessionRecord(this.db, 'bootstrap'),
    ]);
    this.tokens = tokens ?? null;
    let status: SessionStatus = 'anonymous';
    let expired = false;
    if (state?.status === 'revoked') status = 'revoked';
    else if (state?.status === 'suspended') status = 'suspended';
    else if (tokens !== undefined && profile !== undefined) {
      status = profile.me.password_change_required ? 'password_change_required' : 'authenticated';
    }
    // INC-13: a support session past its hour is over before anything shows.
    const supportEndsAt = tokens?.supportEndsAt;
    if (supportEndsAt !== undefined && Date.parse(supportEndsAt) <= this.now()) {
      await this.forgetSession();
      status = 'anonymous';
      expired = true;
    }
    this.setSnapshot({
      status,
      expired,
      support:
        this.tokens?.supportEndsAt === undefined ? null : { endsAt: this.tokens.supportEndsAt },
      profile: expired ? null : (profile?.me ?? null),
      deviceId: device?.deviceId ?? null,
      deviceLabel: device?.label ?? null,
      bootstrap: bootstrap?.document ?? null,
      bootstrapStatus: bootstrap === undefined ? 'loading' : 'ok',
      online: this.isOnline(),
    });
    this.scheduleRefresh();
    void this.loadBootstrap();
    void this.renewProfile();
  }

  dispose(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.onOnline);
      window.removeEventListener('offline', this.onOffline);
    }
    this.document?.removeEventListener('visibilitychange', this.onVisibility);
    if (this.refreshTimer !== null) clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
  }

  /** The tenant's public document, by host. Kept locally for the access screen offline. */
  async loadBootstrap(): Promise<void> {
    try {
      const document = await this.api.get<BootstrapRead>(BOOTSTRAP_PATH, { auth: false });
      await this.db.session.put({ key: 'bootstrap', document, fetchedAt: this.now() });
      this.setSnapshot({ bootstrap: document, bootstrapStatus: 'ok' });
    } catch (error) {
      if (isApiError(error) && error.status === 404) {
        await this.db.session.delete('bootstrap');
        this.setSnapshot({ bootstrap: null, bootstrapStatus: 'unknown_tenant' });
        return;
      }
      if (this.snapshot.bootstrap === null) this.setSnapshot({ bootstrapStatus: 'unavailable' });
    }
  }

  // --- Login, profile, password ------------------------------------------------------

  async login(email: string, password: string, deviceLabel: string): Promise<void> {
    const device = await getSessionRecord(this.db, 'device');
    const body: Record<string, unknown> = {
      email,
      password,
      device_label: deviceLabel,
    };
    if (device !== undefined) body.device_id = device.deviceId;
    let pair: TokenPair;
    try {
      pair = await this.api.post<TokenPair>(LOGIN_PATH, body, { auth: false });
    } catch (error) {
      if (isApiError(error) && error.code === CODES.organizationSuspended) {
        await this.enterState('suspended');
      }
      throw error;
    }
    await this.enter(pair, deviceLabel);
  }

  /** INC-12c-2 (D-291): ask for a link to choose a new password. The answer is
   *  the same whether the address names anybody or not, so nothing comes back;
   *  nothing of the device is touched. */
  async requestPasswordRecovery(email: string): Promise<void> {
    await this.api.post<undefined>(RECOVERY_PATH, { email }, { auth: false });
  }

  /** INC-13 (CA-PLT-05): the support link exchanged for a support session of
   *  the office. Whatever session the tab had goes, the device stays, and so
   *  does every queue: the support session queues nothing and touches nothing
   *  of the field's. A refused link leaves everything as it was. */
  async enterSupport(secret: string): Promise<void> {
    let session: SupportSession;
    try {
      session = await this.api.post<SupportSession>(
        SUPPORT_PATH,
        { support_token: secret },
        { auth: false },
      );
    } catch (error) {
      if (isApiError(error) && error.code === CODES.organizationSuspended) {
        await this.enterState('suspended');
      }
      throw error;
    }
    const issuedAt = this.now();
    this.tokens = {
      key: 'tokens',
      accessToken: session.access_token,
      refreshToken: '',
      expiresIn: session.expires_in,
      expiresAt: issuedAt + session.expires_in * 1000,
      issuedAt,
      supportEndsAt: session.expires_at,
    };
    const me = supportProfile(session);
    await this.db.session.put(this.tokens);
    await this.db.session.put({ key: 'profile', me, loadedAt: issuedAt });
    await this.db.session.delete('state');
    this.setSnapshot({
      status: 'authenticated',
      profile: me,
      support: { endsAt: session.expires_at },
      supportRefusedAt: null,
      expired: false,
    });
    this.scheduleRefresh();
  }

  /** INC-13: the support session is over, at its hour or because the server
   *  says so; back to the access screen with the reason kept. */
  private async endSupport(): Promise<void> {
    await this.forgetSession();
    this.setSnapshot({ status: 'anonymous', profile: null, expired: true });
  }

  /** The tokens and the profile go, and the support with them; nothing else. */
  private async forgetSession(): Promise<void> {
    await this.clearTokens();
    await this.db.session.delete('profile');
    this.setSnapshot({ support: null, supportRefusedAt: null });
  }

  /** INC-13: the person moved on from the write the support session was refused. */
  clearSupportRefusal(): void {
    if (this.snapshot.supportRefusedAt !== null) this.setSnapshot({ supportRefusedAt: null });
  }

  /** INC-07c-1 (D-258): the person chooses a password with their link and
   *  enters with it. It opens a session exactly as a login does, with the
   *  device named the same way; a refusal of the link leaves whatever the
   *  device already had as it was. */
  async acceptInvitation(token: string, password: string, deviceLabel: string): Promise<void> {
    const device = await getSessionRecord(this.db, 'device');
    const body: Record<string, unknown> = {
      invitation_token: token,
      new_password: password,
      device_label: deviceLabel,
    };
    if (device !== undefined) body.device_id = device.deviceId;
    let pair: TokenPair;
    try {
      pair = await this.api.post<TokenPair>(INVITATION_PATH, body, { auth: false });
    } catch (error) {
      if (isApiError(error) && error.code === CODES.organizationSuspended) {
        await this.enterState('suspended');
      }
      throw error;
    }
    await this.enter(pair, deviceLabel);
  }

  /** What a login and an accepted link have in common: the tokens and the
   *  device kept, the profile read, this person's refusals queued again. */
  private async enter(pair: TokenPair, deviceLabel: string): Promise<void> {
    await this.storeTokens(pair);
    await this.db.session.put({ key: 'device', deviceId: pair.device_id, label: deviceLabel });
    await this.db.session.delete('state');
    // INC-13: a person's session replaces a support session the tab had.
    this.setSnapshot({
      deviceId: pair.device_id,
      deviceLabel,
      expired: false,
      support: null,
      supportRefusedAt: null,
    });
    await this.loadProfile();
    const author = this.snapshot.profile?.user.id;
    if (author !== undefined) await this.retryRejected(author);
  }

  async loadProfile(): Promise<void> {
    const me = await this.api.get<MeResponse>(ME_PATH);
    await this.keepProfile(me);
  }

  /** The profile stored and in the snapshot, in place of the one before. */
  private async keepProfile(me: MeResponse): Promise<void> {
    await this.db.session.put({ key: 'profile', me, loadedAt: this.now() });
    this.setSnapshot({
      profile: me,
      status: me.password_change_required ? 'password_change_required' : 'authenticated',
    });
  }

  /** INC-16.1 (D-424): the profile read again in the background, for a person's
   *  session that is open, with signal; a support session has no profile to
   *  read and is left alone. One reading at a time.
   *
   *  The answer replaces the stored profile and nothing else: no token, no
   *  queue, no draft, no photo, nothing prepared for the day. With no signal
   *  or a server that failed what is stored stays, said nowhere and asked for
   *  again at the next moment that reads it, never sooner. A 401 takes the
   *  path every request takes (api/client.ts): refreshed and asked again, and
   *  a refresh refused ends the session with everything local kept. */
  renewProfile(): Promise<void> {
    if (this.renewal !== null) return this.renewal;
    if (!this.reads() || !this.isOnline()) return Promise.resolve();
    this.renewal = this.doRenewProfile().finally(() => {
      this.renewal = null;
    });
    return this.renewal;
  }

  private async doRenewProfile(): Promise<void> {
    let me: MeResponse;
    try {
      me = await this.api.get<MeResponse>(ME_PATH);
    } catch (error) {
      if (isNetworkError(error) || isApiError(error)) return;
      throw error;
    }
    await this.adoptProfile(me);
  }

  /** INC-16.1 (D-424): a reading of the profile made elsewhere — the office's
   *  frame reads it each time a section opens and when the tab comes back —
   *  replaces the stored one as the session's own readings do. Taken only by
   *  the open session of the person it names. */
  async adoptProfile(me: MeResponse): Promise<void> {
    if (!this.reads() || this.snapshot.profile?.user.id !== me.user.id) return;
    await this.keepProfile(me);
  }

  /** Whether this session's profile is one to read again: a person's, open. */
  private reads(): boolean {
    return (
      this.tokens !== null &&
      this.tokens.supportEndsAt === undefined &&
      isOpen(this.snapshot.status)
    );
  }

  /** Every item of this operator the server refused for good is queued again
   *  (D-176): a rejection is never discarded and is retried at the operator's
   *  next sign in, when what the office changed may have been resolved.
   *  Another operator's rejections are not touched. */
  async retryRejected(authorId: string): Promise<number> {
    const at = this.now();
    return this.db.outbox_data
      .where('author_id')
      .equals(authorId)
      .filter((item) => item.status === 'rejected')
      .modify({ status: 'queued', next_attempt_at: null, updated_at: at });
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.api.post<undefined>(PASSWORD_PATH, {
      current_password: currentPassword,
      new_password: newPassword,
    });
    await this.loadProfile();
  }

  // --- Tokens ----------------------------------------------------------------------------

  private async storeTokens(pair: TokenPair): Promise<void> {
    const issuedAt = this.now();
    this.tokens = {
      key: 'tokens',
      accessToken: pair.access_token,
      refreshToken: pair.refresh_token,
      expiresIn: pair.expires_in,
      expiresAt: issuedAt + pair.expires_in * 1000,
      issuedAt,
    };
    await this.db.session.put(this.tokens);
    this.scheduleRefresh();
  }

  private async clearTokens(): Promise<void> {
    this.tokens = null;
    await this.db.session.delete('tokens');
    this.scheduleRefresh();
  }

  /** True when less than the configured fraction of the token's life remains. */
  refreshDue(): boolean {
    if (this.tokens === null) return false;
    const remaining = this.tokens.expiresAt - this.now();
    return remaining <= this.tokens.expiresIn * 1000 * this.refreshAheadFraction;
  }

  private scheduleRefresh(): void {
    if (this.refreshTimer !== null) clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
    if (this.tokens === null) return;
    // INC-13: a support session is never refreshed; it ends at its hour.
    const supportEndsAt = this.tokens.supportEndsAt;
    if (supportEndsAt !== undefined) {
      const delay = Math.max(0, Date.parse(supportEndsAt) - this.now());
      this.refreshTimer = setTimeout(() => {
        this.refreshTimer = null;
        void this.endSupport();
      }, delay);
      return;
    }
    const fireAt = this.tokens.expiresAt - this.tokens.expiresIn * 1000 * this.refreshAheadFraction;
    const delay = Math.max(0, fireAt - this.now());
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      if (this.isOnline()) void this.refresh();
      // Offline: the online listener refreshes as soon as connectivity returns.
    }, delay);
  }

  /** One refresh in flight at a time. Resolves to the new access token, or
   *  null when the refresh was refused and the state changed accordingly. */
  refresh(): Promise<string | null> {
    if (this.refreshInflight !== null) return this.refreshInflight;
    this.refreshInflight = this.doRefresh().finally(() => {
      this.refreshInflight = null;
    });
    return this.refreshInflight;
  }

  private async doRefresh(): Promise<string | null> {
    if (this.tokens === null) return null;
    // INC-13: a support session the server no longer takes has nothing to
    // refresh with: it is over.
    if (this.tokens.supportEndsAt !== undefined) {
      await this.endSupport();
      return null;
    }
    if (!this.isOnline()) return null;
    try {
      const pair = await this.api.post<TokenPair>(
        REFRESH_PATH,
        { refresh_token: this.tokens.refreshToken },
        { auth: false },
      );
      await this.storeTokens(pair);
      return pair.access_token;
    } catch (error) {
      if (isNetworkError(error)) return null;
      if (isApiError(error)) {
        await this.handleSessionCode(error.code, error.status);
        return null;
      }
      throw error;
    }
  }

  // --- The contract table -------------------------------------------------------------------

  async handleSessionCode(code: string, status: number): Promise<void> {
    switch (code) {
      case CODES.organizationSuspended:
        await this.enterState('suspended');
        return;
      case CODES.deviceRevoked:
        await this.clearTokens();
        await this.enterState('revoked');
        return;
      case CODES.refreshInvalid:
      case CODES.refreshExpired:
        // Back to the access screen with everything local intact, and the
        // reason kept: the server ended this session, the person did not.
        await this.clearTokens();
        this.setSnapshot({ status: 'anonymous', expired: true });
        return;
      case CODES.passwordChangeRequired:
        this.setSnapshot({ status: 'password_change_required' });
        return;
      case CODES.supportReadOnly:
        // INC-13: the caller shows its own failure; the office says why.
        this.setSnapshot({ supportRefusedAt: this.now() });
        return;
      case CODES.tokenMissing:
      case CODES.tokenInvalid:
      case CODES.tokenStale:
        // Reached only when the refresh that follows was refused; that
        // refusal has already set the state. Nothing more to do here.
        return;
      default:
        // permission_denied and anything else: the caller shows it. A 401
        // with an unknown code still means the token is gone.
        // A refused link says nothing about a session either (INC-07c-1).
        if (
          status === 401 &&
          code !== CODES.invalidCredentials &&
          !INVITATION_REFUSALS.has(code) &&
          !SUPPORT_REFUSALS.has(code)
        ) {
          await this.clearTokens();
          this.setSnapshot({ status: 'anonymous', expired: true });
        }
    }
  }

  private async enterState(status: 'revoked' | 'suspended'): Promise<void> {
    await this.db.session.put({ key: 'state', status, since: this.now() });
    this.setSnapshot({ status });
  }

  /** Leave a terminal state and go to the access screen; the queue stays. */
  async acknowledgeState(): Promise<void> {
    await this.db.session.delete('state');
    await this.clearTokens();
    this.setSnapshot({ status: 'anonymous', expired: false });
  }

  // --- Sign out -----------------------------------------------------------------------------

  /** What the signed-in operator still has on the device, of both queues. */
  async pending(): Promise<number> {
    return ownPendingCount(this.db, this.snapshot.profile?.user.id ?? null);
  }

  /** What blocks the sign out: the operator's own work that only waits for
   *  signal (D-176). A rejection the server will not take is not waiting for
   *  anything, so it does not block, and it is not discarded either. */
  async waiting(): Promise<number> {
    return ownWaitingCount(this.db, this.snapshot.profile?.user.id ?? null);
  }

  /** Refused with the count while the operator's own work waits for signal.
   *  Otherwise: the session goes, and of the local stores only what is this
   *  operator's and settled. What is kept, and why:
   *
   *  * its own rejected items, the drafts they belong to and their photos,
   *    because nothing the operator loaded is ever discarded (D-176);
   *  * every record of another operator, untouched, because it is not this
   *    session's to end (D-187);
   *  * the prepared data, the agenda and the device, as before, and with the
   *    device the invitation to install it already dismissed (D-208). */
  async signOut(): Promise<void> {
    const author = this.snapshot.profile?.user.id ?? null;
    const waiting = await this.waiting();
    if (waiting > 0) throw new SignOutBlockedError(waiting);
    // INC-13: a support session ends no device on the server, which would
    // refuse it as a write; it is simply forgotten here.
    if (this.tokens !== null && this.tokens.supportEndsAt === undefined && this.isOnline()) {
      try {
        await this.api.post<undefined>(LOGOUT_PATH, undefined);
      } catch (error) {
        if (!(error instanceof NetworkError) && !(error instanceof ApiError)) throw error;
      }
    }
    this.tokens = null;
    this.scheduleRefresh();
    await this.db.transaction(
      'rw',
      [
        this.db.session,
        this.db.visit_drafts,
        this.db.outbox_data,
        this.db.sync_log,
        this.db.photo_queue,
      ],
      async () => {
        const keep = await Promise.all([
          this.db.session.get('device'),
          this.db.session.get('install'),
        ]);
        await this.db.session.clear();
        for (const record of keep) if (record !== undefined) await this.db.session.put(record);
        const mine = (owner: string | null): boolean => owner === author;
        const items = await this.db.outbox_data.toArray();
        const kept = items.filter((item) => !mine(item.author_id) || item.status === 'rejected');
        const gone = items.filter((item) => !kept.includes(item));
        const keptVisits = new Set(kept.map((item) => item.client_generated_id));
        await this.db.outbox_data.bulkDelete(gone.map((item) => item.id));
        const goneIds = new Set(gone.map((item) => item.id));
        await this.db.sync_log.filter((entry) => goneIds.has(entry.outbox_id)).delete();
        const drafts = await this.db.visit_drafts.toArray();
        await this.db.visit_drafts.bulkDelete(
          drafts
            .filter((draft) => mine(draft.author_id) && !keptVisits.has(draft.client_generated_id))
            .map((draft) => draft.client_generated_id),
        );
        // INC-05b: reached with nothing of this operator waiting, so its own
        // photos are uploaded — except those of a visit whose item was refused,
        // which stay with it.
        const photos = await this.db.photo_queue.toArray();
        await this.db.photo_queue.bulkDelete(
          photos
            .filter(
              (photo) => mine(photo.author_id) && !keptVisits.has(photo.visit_client_generated_id),
            )
            .map((photo) => photo.client_generated_id),
        );
      },
    );
    this.setSnapshot({
      status: 'anonymous',
      profile: null,
      expired: false,
      support: null,
      supportRefusedAt: null,
    });
    void this.loadBootstrap();
  }
}
